import {
  collection, doc, addDoc, updateDoc, setDoc, getDoc,
  query, where, getDocs, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db, auth } from './firebase';

// Deterministic id for the per-(editor,project) access record. Firestore rules
// can only exists()/get() a specific path (they can't run queries), so edit
// access is recorded at this exact document id for the project-write rule to
// check: exists(share_codes/{editorUid}_{projectId}).
const accessId = (editorUid, projectId) => `${editorUid}_${projectId}`;

// ─── SHARE CODES ──────────────────────────────────────────────────────────────
// A "share code" grants ONE user edit access to ONE shared project:
//   1. The would-be editor generates a code from their user menu. It's stored in
//      `share_codes` carrying THEIR userID, status 'free', and a 10-minute expiry.
//   2. They hand the code to the project owner, who pastes it into the Share
//      modal's "Editable?" code box. Redeeming flips the code to 'used' and
//      stamps the projectID onto it — binding editor ↔ project.
//   3. When that editor opens the /share link (while logged in), the shared page
//      confirms a used code exists for their userID + this projectID and, if so,
//      opens the design in edit mode.
//
// Firestore doc shape: { userID, userCode, status: 'free'|'used', projectID, expireAt, createdAt }

const CODE_LEN = 16;
const CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const CODE_TTL_MS = 10 * 60 * 1000; // 10 minutes

function generateCode() {
  const rnd = new Uint32Array(CODE_LEN);
  (window.crypto || window.msCrypto).getRandomValues(rnd);
  let out = '';
  for (let i = 0; i < CODE_LEN; i++) out += CODE_CHARS[rnd[i] % CODE_CHARS.length];
  return out;
}

// Editor: mint a fresh code tied to their own uid (expires in 10 min). Returns
// the code string to show/copy.
export async function generateShareCode(userId) {
  const userCode = generateCode();
  await addDoc(collection(db, 'share_codes'), {
    userID: userId,
    userCode,
    status: 'free',
    projectID: null,
    expireAt: Timestamp.fromMillis(Date.now() + CODE_TTL_MS),
    createdAt: serverTimestamp(),
  });
  return userCode;
}

// Owner: redeem an editor's code for a project. Returns:
//   { ok: true, userID }                    — bound editor↔project, marked used
//   { ok: false, reason: 'not_found' }      — no such code
//   { ok: false, reason: 'expired' }        — past its 10-minute window
//   { ok: false, reason: 'used' }           — already redeemed
export async function redeemShareCode(userCode, projectId) {
  const code = (userCode || '').trim();
  if (!code) return { ok: false, reason: 'not_found' };

  const snap = await getDocs(query(collection(db, 'share_codes'), where('userCode', '==', code)));
  if (snap.empty) return { ok: false, reason: 'not_found' };

  const d = snap.docs[0];
  const data = d.data();
  const exp = data.expireAt?.toMillis?.() ?? 0;
  if (exp && exp < Date.now()) return { ok: false, reason: 'expired' };
  if (data.status === 'used') return { ok: false, reason: 'used' };

  // Grant access FIRST (the deterministic record the project-write rule checks),
  // and only mark the code used once that succeeded — so if the grant write is
  // rejected (e.g. rules), the code stays 'free' and the owner can retry instead
  // of being stuck with a code that reads as already-used.
  try {
    await setDoc(doc(db, 'share_codes', accessId(data.userID, projectId)), {
      userID: data.userID,
      projectID: projectId,
      status: 'used',
      grantedBy: auth.currentUser?.uid || null,
      // Carry the code's expiry onto the access record so edit access is
      // time-limited — once it passes, the editor must get a fresh code.
      expireAt: data.expireAt || null,
      createdAt: serverTimestamp(),
    });
    await updateDoc(doc(db, 'share_codes', d.id), { status: 'used', projectID: projectId });
  } catch (err) {
    console.error('[ShareCode] grant write failed:', err);
    return { ok: false, reason: 'error' };
  }
  return { ok: true, userID: data.userID };
}

// Editor opening a shared link: resolve edit access + its expiry.
//   { granted: true,  expireAt } — active access (expireAt ms, or null if none)
//   { granted: false, expired: true, expireAt } — access existed but has lapsed
//   { granted: false, expired: false } — no access record at all
// Reads the deterministic access doc directly (same path the Firestore rules
// check), so no query/index is involved.
export async function getEditAccess(userId, projectId) {
  if (!userId || !projectId) return { granted: false, expired: false };
  try {
    const snap = await getDoc(doc(db, 'share_codes', accessId(userId, projectId)));
    if (!snap.exists() || snap.data().status !== 'used') return { granted: false, expired: false };
    const exp = snap.data().expireAt?.toMillis?.() ?? 0;
    // Access must have a live expiry. Missing (stale grant from before expiry
    // existed) or past → expired, so the editor is forced to get a fresh code.
    if (!exp || exp < Date.now()) return { granted: false, expired: true, expireAt: exp || null };
    return { granted: true, expired: false, expireAt: exp };
  } catch {
    return { granted: false, expired: false };
  }
}
